import {
  alphabetRegExp,
  alphaNumericRegexp,
  specialCharRegexp,
  startsWithNumberRegex,
} from "./regex";

/* -------------------------------------------------------------------------- */
/*                          Get Trimmed String                                */
/* -------------------------------------------------------------------------- */
export const trimString = (str: string) => {
  if (typeof str !== "string") return "";

  return str.trim();
};

/* -------------------------------------------------------------------------- */
/*                   Get the env variable using the key                       */
/* -------------------------------------------------------------------------- */
// export const getEnvVar = (key: string): string => {
//   const value: string = trimString(import.meta.env[key as keyof ImportMetaEnv] || "");

//   if (!value) {
//     console.warn(`Environment variable ${key} is not set.`);
//     return "";
//   }

//   return value;
// };

/* -------------------------------------------------------------------------- */
/*                   Filter out the subCategories and Id                      */
/* -------------------------------------------------------------------------- */
type SubCategoryOption = {
  label: string;
  value: string;
};

type OrgCategoryRecord = {
  org_category_pid?: string;
  org_category_id?: string;
  _id?: string;
  name?: string;
  sub_category?: string;
  description?: string;
  is_visible?: boolean;
  is_deleted?: boolean;
};

type OrgCategoryListResponse =
  | OrgCategoryRecord[]
  | {
      categories?: OrgCategoryRecord[];
    }
  | null
  | undefined;

/** True when a category field looks like a generated id/slug, not a display label. */
function isTechnicalCategoryLabel(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;

  if (/^(category_|sub_|desc_|orgcat_)/i.test(trimmed)) return true;
  if (/^[a-f0-9]{24}$/i.test(trimmed)) return true;
  if (/^org_[a-z0-9_]+$/i.test(trimmed) && !trimmed.includes(" ")) return true;

  return false;
}

/** Picks a human-readable label for signup dropdowns (never shows technical ids). */
function formatCategoryDisplayLabel(item: OrgCategoryRecord): string {
  const name = (item.name || "").trim();
  const subCategory = (item.sub_category || "").trim();
  const description = (item.description || "").trim();

  const readableName = isTechnicalCategoryLabel(name) ? "" : name;
  const readableSub = isTechnicalCategoryLabel(subCategory) ? "" : subCategory;
  const readableDesc = isTechnicalCategoryLabel(description) ? "" : description;

  // Match legacy onboarding: prefer sub-category label (e.g. "eCommerce").
  if (readableSub) return readableSub;
  if (readableName) return readableName;
  if (readableDesc) return readableDesc;

  return "";
}

export const getSubCategoryOptions = (
  data: OrgCategoryListResponse
): SubCategoryOption[] => {
  const categories = Array.isArray(data)
    ? data
    : data?.categories ||
      (data as { data?: { categories?: OrgCategoryRecord[] } })?.data
        ?.categories ||
      [];

  return categories
    .filter((item) => item?.is_visible !== false && item?.is_deleted !== true)
    .map((item) => {
      const value = (
        item.org_category_pid ||
        item.org_category_id ||
        item._id ||
        ""
      ).trim();
      const label = formatCategoryDisplayLabel(item);

      return { label, value };
    })
    .filter(
      (option) =>
        option.label &&
        option.value &&
        !isTechnicalCategoryLabel(option.label)
    );
};

/* -------------------------------------------------------------------------- */
/*                   Validate for string                                 */
/* -------------------------------------------------------------------------- */
export function isValidName(
  name: string,
  includeNumericChar: boolean
): boolean {
  if (includeNumericChar) {
    return alphaNumericRegexp.test(name) && !specialCharRegexp.test(name);
  }
  return alphabetRegExp.test(name) && !specialCharRegexp.test(name);
}

/* -------------------------------------------------------------------------- */
/*                   Validate string starts with number                       */
/* -------------------------------------------------------------------------- */
export function startsWithNumber(name: string) {
  return startsWithNumberRegex.test(name);
}
